/* eslint-disable no-underscore-dangle */
import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  NormalizedCacheObject,
} from '@apollo/client'

import { refreshTokenMutation } from '@/gql/system/authentication/authMutations.systemGraphql'
import {
  SessionKey,
  sessionStorageService,
} from '@/services/SessionStorage/SessionStorageService'
import { AuthRefreshMutation } from '@/gql/systemApi'

type PendingRequest = {
  resolve: (token: string) => void
  reject: (error: unknown) => void
}

const PROACTIVE_REFRESH_BUFFER_MS = 5 * 60 * 1000 // 5 minutes before expiry
const PRE_REQUEST_BUFFER_S = 30 // refresh if token expires within 30s

/**
 * Rejects null, undefined, "null", "undefined", and empty strings.
 * Fixes Bug #2: String(null) → "null" token corruption.
 */
function isValidTokenString(value: unknown): value is string {
  if (typeof value !== 'string') return false
  const trimmed = value.trim()
  return trimmed !== '' && trimmed !== 'null' && trimmed !== 'undefined'
}

/**
 * Converts base64url to standard base64 (JWTs use base64url per RFC 7519).
 */
function base64UrlDecode(str: string): string {
  let base64 = str.replace(/-/g, '+').replace(/_/g, '/')
  const pad = base64.length % 4
  if (pad) {
    base64 += '='.repeat(4 - pad)
  }
  return atob(base64)
}

const NO_REFRESH_TOKEN = 'NoRefreshTokenError'

/** The session cannot be refreshed because no refresh token is stored. */
function noRefreshTokenError(): Error {
  const error = new Error('No refresh token available')
  error.name = NO_REFRESH_TOKEN
  return error
}

/**
 * True when the server actually rejected the session: Directus answers a bad
 * refresh token with a GraphQL error ("Invalid user credentials.", HTTP 200)
 * or an HTTP 401/403. Anything else (offline, timeout, 5xx, parse error) is a
 * transient failure that says nothing about whether the session is valid.
 */
function isSessionRejected(error: unknown): boolean {
  if (error instanceof Error && error.name === NO_REFRESH_TOKEN) return true
  if (!error || typeof error !== 'object') return false
  const { graphQLErrors, networkError } = error as {
    graphQLErrors?: ReadonlyArray<unknown>
    networkError?: { statusCode?: number } | null
  }
  if (graphQLErrors && graphQLErrors.length > 0) return true
  const status = networkError?.statusCode
  return status === 401 || status === 403
}

/**
 * Decodes the JWT payload and returns the `exp` claim (Unix seconds).
 * No secret needed — we only read the expiry, not verify the signature.
 */
function getJwtExpiry(token: string): number | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const payload = JSON.parse(base64UrlDecode(parts[1]))
    return typeof payload.exp === 'number' ? payload.exp : null
  } catch {
    return null
  }
}

class TokenManager {
  private isRefreshing = false

  private destroyed = false

  private pendingRequests: PendingRequest[] = []

  private proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null

  private clientForRefreshToken: ApolloClient<NormalizedCacheObject>

  private boundOnStorageChange: (e: StorageEvent) => void

  private sessionClearedListeners = new Set<() => void>()

  constructor() {
    const httpAuthLink = createHttpLink({
      uri: `${import.meta.env.VITE_API_URL}/system`,
    })

    this.clientForRefreshToken = new ApolloClient({
      cache: new InMemoryCache(),
      link: httpAuthLink,
      connectToDevTools: false,
    })

    // Listen for cross-tab localStorage changes. The `storage` event fires
    // in OTHER tabs when localStorage is modified, allowing us to detect
    // when another tab logs in as a different user and gracefully redirect.
    // This is the SOLE mechanism for cross-tab session detection — no
    // circular callbacks between TokenManager and SessionStorageService.
    this.boundOnStorageChange = this.onStorageChange.bind(this)
    window.addEventListener('storage', this.boundOnStorageChange)
  }

  getAccessToken(): string | null {
    const token = sessionStorageService.getItem(SessionKey.TOKEN)
    return isValidTokenString(token) ? token : null
  }

  getRefreshToken(): string | null {
    const token = sessionStorageService.getItem(SessionKey.REFRESH_TOKEN)
    return isValidTokenString(token) ? token : null
  }

  /**
   * Returns a valid (non-expired) access token. If the current token is
   * expired or about to expire, refreshes first so the caller never sends
   * an expired JWT. This eliminates TOKEN_EXPIRED errors from Datadog
   * because the expired token never leaves the browser.
   */
  async getValidAccessToken(): Promise<string | null> {
    const token = this.getAccessToken()
    if (!token) return null

    const exp = getJwtExpiry(token)
    if (exp === null) return token // can't decode → let the server decide

    const nowSeconds = Math.floor(Date.now() / 1000)
    if (exp - nowSeconds <= PRE_REQUEST_BUFFER_S) {
      // Token expired or about to expire — refresh before sending
      try {
        return await this.refreshToken()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.debug(
          'Pre-flight token refresh failed, falling back to reactive path:',
          error
        )
        return null
      }
    }

    return token
  }

  /**
   * Validates and stores both tokens. Returns false if either is invalid,
   * preventing corrupted state from entering storage.
   * Automatically schedules proactive refresh based on the JWT's actual expiry.
   */
  setTokens(accessToken: unknown, refreshToken: unknown): boolean {
    if (!isValidTokenString(accessToken) || !isValidTokenString(refreshToken)) {
      return false
    }

    // Re-activate after destroy/failure so the singleton works across
    // sign-out → sign-in cycles without a full page reload (SignOut uses
    // React Router navigate(), which keeps the same JS context).
    if (this.destroyed) {
      this.destroyed = false
      // addEventListener is a no-op if the same listener reference is
      // already registered, so this is safe even if destroy() wasn't called.
      window.addEventListener('storage', this.boundOnStorageChange)
    }

    sessionStorageService.setItem(SessionKey.TOKEN, accessToken)
    sessionStorageService.setItem(SessionKey.REFRESH_TOKEN, refreshToken)

    // Auto-schedule proactive refresh from the JWT's actual exp claim.
    // This is the single source of truth for scheduling — refreshToken()
    // defers to this via setTokens() and does not schedule separately.
    const exp = getJwtExpiry(accessToken)
    if (exp !== null) {
      const msUntilExpiry = (exp - Math.floor(Date.now() / 1000)) * 1000
      if (msUntilExpiry > 0) {
        this.scheduleProactiveRefresh(msUntilExpiry)
      }
    }

    return true
  }

  /**
   * Single-gate refresh: only one in-flight refresh at a time.
   * All concurrent callers queue behind the same Promise.
   * On failure: rejects all pending, preserves persistent storage, redirects to /signIn.
   */
  async refreshToken(): Promise<string> {
    if (this.destroyed) {
      throw new Error('TokenManager destroyed')
    }

    if (this.isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        this.pendingRequests.push({ resolve, reject })
      })
    }

    this.isRefreshing = true

    const storedRefreshToken = this.getRefreshToken()

    if (!storedRefreshToken) {
      // Nothing to refresh with: the session cannot be recovered, so treat
      // this like a rejection and drop the orphaned access token.
      const error = noRefreshTokenError()
      this.handleRefreshFailure(error, null)
      throw error
    }

    try {
      const { data } =
        await this.clientForRefreshToken.mutate<AuthRefreshMutation>({
          mutation: refreshTokenMutation,
          variables: { refreshToken: storedRefreshToken },
          // A rejected refresh comes back as HTTP 200 with a GraphQL error
          // (e.g. "Invalid user credentials."). 'none' is Apollo's default for
          // mutations, but spelling it out keeps that a failure on purpose.
          errorPolicy: 'none',
        })

      const newAccessToken = data?.auth_refresh?.access_token
      const newRefreshToken = data?.auth_refresh?.refresh_token

      // setTokens() handles validation, storage, and proactive refresh scheduling
      if (!this.setTokens(newAccessToken, newRefreshToken)) {
        throw new Error('Refresh returned invalid tokens')
      }

      // Resolve all queued requests with the new token
      const validToken = newAccessToken as string
      this.pendingRequests.forEach(({ resolve }) => resolve(validToken))
      this.pendingRequests = []
      this.isRefreshing = false

      return validToken
    } catch (error) {
      this.handleRefreshFailure(error, storedRefreshToken)
      throw error
    }
  }

  /**
   * Schedules a proactive refresh before the token expires.
   * Silent failure — next real request falls back to the reactive errorLink path.
   */
  private scheduleProactiveRefresh(expiresMs: number): void {
    this.clearProactiveRefreshTimer()

    let delay = expiresMs - PROACTIVE_REFRESH_BUFFER_MS
    if (delay <= 0) {
      // Token lifetime is shorter than the buffer; refresh at 75% of lifetime
      delay = Math.max(Math.floor(expiresMs * 0.75), 1000)
    }

    this.proactiveRefreshTimer = setTimeout(() => {
      this.refreshToken().catch(() => {
        // Silent failure: reactive path handles it on next request
      })
    }, delay)
  }

  /**
   * Per-operation retry budget using Apollo operation context.
   * Prevents infinite errorLink → refresh → errorLink loops (Bug #3).
   */
  hasExhaustedRetryBudget(operation: {
    getContext: () => Record<string, unknown>
  }): boolean {
    return !!operation.getContext().__refreshAttempted
  }

  markRetryAttempted(operation: {
    setContext: (
      updater: (prev: Record<string, unknown>) => Record<string, unknown>
    ) => void
  }): void {
    operation.setContext((prev: Record<string, unknown>) => ({
      ...prev,
      __refreshAttempted: true,
    }))
  }

  /**
   * True once the manager has been torn down (logout, session invalidation,
   * or a failed refresh that redirected to /signIn). Callers use this to
   * avoid triggering a refresh after teardown, which would throw
   * "TokenManager destroyed" and surface as an uncaught error (ENG-1633).
   */
  isDestroyed(): boolean {
    return this.destroyed
  }

  /**
   * Clears timer and state. Called on logout.
   */
  destroy(): void {
    this.destroyed = true
    this.clearProactiveRefreshTimer()
    window.removeEventListener('storage', this.boundOnStorageChange)
    this.isRefreshing = false
    const error = new Error('TokenManager destroyed')
    this.pendingRequests.forEach(({ reject }) => reject(error))
    this.pendingRequests = []
  }

  /**
   * Registers a callback for when this manager drops the stored session after
   * a rejected refresh. The Apollo cache uses it to forget its memoized
   * `isLoggedIn` value (see src/apollo/index.ts); this module cannot import
   * the cache itself without a circular dependency.
   */
  onSessionCleared(listener: () => void): () => void {
    this.sessionClearedListeners.add(listener)
    return () => {
      this.sessionClearedListeners.delete(listener)
    }
  }

  /**
   * @param failedRefreshToken The refresh token that was rejected, or null when
   *   there was none.
   */
  private handleRefreshFailure(
    error: unknown,
    failedRefreshToken: string | null
  ): void {
    // Reject all queued requests (Bug #4 fix)
    this.pendingRequests.forEach(({ reject }) => reject(error))
    this.pendingRequests = []
    this.isRefreshing = false

    if (this.destroyed) return

    // The stored refresh token has changed since this refresh started: a
    // sign-in completed meanwhile (same tab after sign-out, or another tab).
    // This rejection is about the old session, so leave the new one alone.
    if (this.getRefreshToken() !== failedRefreshToken) return

    // A network failure (offline, 5xx) says nothing about the session. Leave
    // the tokens in place so the next request or reload can retry, instead of
    // wiping a valid session and, via the storage event, every other tab.
    if (!isSessionRejected(error)) return

    this.destroyed = true
    this.clearProactiveRefreshTimer()

    // Drop the dead session, including on /signIn. Before ENG-1932 the /signIn
    // case returned early here on the assumption that the SignIn page clears
    // storage on mount. It doesn't (it only destroys this manager), and it
    // never mounts for this user anyway: the router only renders after the
    // boot-time UsersMe query settles, i.e. after this refresh has already
    // failed, and AuthRoute treats any stored token as "logged in" and forwards
    // /signIn into the app. The stale tokens then made every request go out
    // anonymous, so the user got an empty app instead of the sign-in form.
    const persistentValues = sessionStorageService.getPersistentValues()
    sessionStorageService.clear()
    sessionStorageService.restorePersistentValues(persistentValues)
    this.sessionClearedListeners.forEach(listener => listener())

    // Already on /signIn: no full navigation needed. The listeners above make
    // `isLoggedIn` re-evaluate, so AuthRoute renders the form. Other /signIn
    // tabs ignore the storage event this clear fires (see onStorageChange).
    if (window.location.pathname === '/signIn') return

    // Full page navigation ensures a clean slate (fresh singletons).
    // Uses href instead of reload() to avoid thundering herd (Bug #5 fix).
    window.location.href = '/signIn'
  }

  /**
   * Cleanup for cross-tab session invalidation (another tab logged out or
   * changed users). Cancels timers, clears pending state, and redirects
   * to /signIn. Sets destroyed=true so handleRefreshFailure (if it fires
   * from an in-flight request) skips its own storage clear + redirect.
   */
  private handleSessionInvalidation(): void {
    if (this.destroyed) return
    this.destroyed = true
    this.clearProactiveRefreshTimer()
    this.isRefreshing = false
    this.pendingRequests = []

    if (window.location.pathname === '/signIn') return
    window.location.href = '/signIn'
  }

  /**
   * Handles cross-tab session changes via the `storage` event (async).
   * Covers: another tab logging out (clear) or logging in as different user.
   */
  private onStorageChange(event: StorageEvent): void {
    // Ignore storage events when already on /signIn. The SignIn page clears
    // localStorage on mount, which fires events to other tabs. If those tabs
    // are also on /signIn they'd reload, clear again, and fire events back —
    // creating an infinite reload loop between tabs.
    if (window.location.pathname === '/signIn') return

    // localStorage.clear() fires with key === null
    const isFullClear = event.key === null
    const isEmailChange = event.key === SessionKey.ATTORNEY_EMAIL_KEY

    if (!isFullClear && !isEmailChange) return

    // Another tab cleared storage (logout) or changed the logged-in user
    if (isFullClear || (event.oldValue && event.oldValue !== event.newValue)) {
      this.handleSessionInvalidation()
    }
  }

  private clearProactiveRefreshTimer(): void {
    if (this.proactiveRefreshTimer !== null) {
      clearTimeout(this.proactiveRefreshTimer)
      this.proactiveRefreshTimer = null
    }
  }
}

export const tokenManager = new TokenManager()
